import {
  DashboardOutlined,
  LocationCity as CitiesIcon,
  Sync as LogislyIcon,
  Settings as SettingsIcon,
  Groups as TeamsIcon,
  LocalShipping as TrucksIcon,
  School as TutorialIcon,
  People as UsersIcon,
} from "@mui/icons-material";
import { List } from "@mui/material";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { TestId } from "../../constants/testIds";
import type { RootState } from "../../store";
import type translations from "../../translations/translations.json";
import type { TruckGroup } from "../../types/truckGroup";
import { isUserAdmin, canUseUangJalanRevamp } from "../../types/user";
import SidebarMenuItem from "../SidebarMenuItem";
import DevToolsMenu from "./DevToolsMenu";
import LogislySpxOnCallMenu from "./LogislySpxOnCallMenu";
import StaticValuesMenu from "./StaticValuesMenu";
import TruckGroupsMenu from "./TruckGroupsMenu";
import SuratJalanMenu from "./SuratJalanMenu";
import UangJalanMenu from "./UangJalanMenu";

type TranslationKeys = keyof typeof translations.en;

interface SidebarMenuProps {
  truckGroups: TruckGroup[];
  userId?: string;
  loading: boolean;
  onNavigate?: () => void;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({ truckGroups, userId, loading, onNavigate }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const canManageCompanySettings = isUserAdmin(user);

  const menuItems = [
    {
      text: t("Trucks" as TranslationKeys),
      icon: <TrucksIcon />,
      path: "/trucks",
      isTrucks: true,
      testId: TestId.SIDEBAR.MENU_ITEM_TRUCKS,
    },
    {
      text: "Logisly SPX OnCall",
      icon: <LogislyIcon />,
      path: "/logisly-spx-oncall",
      isLogislySpxOnCall: true,
      testId: TestId.SIDEBAR.MENU_ITEM_LOGISLY_SPX_ONCALL,
    },
    {
      text: t("Users" as TranslationKeys),
      icon: <UsersIcon />,
      path: "/users",
      testId: TestId.SIDEBAR.MENU_ITEM_USERS,
    },
    {
      text: t("Teams" as TranslationKeys),
      icon: <TeamsIcon />,
      path: "/operations-teams",
      testId: TestId.SIDEBAR.MENU_ITEM_OPS_TEAMS,
    },
    ...(canManageCompanySettings
      ? [
          {
            text: t("Company Settings" as TranslationKeys),
            icon: <SettingsIcon />,
            path: "/settings",
            testId: TestId.SIDEBAR.MENU_ITEM_COMPANY_SETTINGS,
          },
        ]
      : []),
    {
      text: t("Static Values" as TranslationKeys),
      icon: <CitiesIcon />,
      path: "/locations",
      isStaticValues: true,
      testId: TestId.SIDEBAR.MENU_ITEM_STATIC_VALUES,
    },
    {
      text: t("Dev Tools" as TranslationKeys),
      path: "/dev-tools",
      isDevTools: true,
      testId: TestId.SIDEBAR.MENU_ITEM_DEV_TOOLS,
    },
    {
      text: t("Tutorial" as TranslationKeys),
      icon: <TutorialIcon />,
      path: "/tutorial",
      testId: TestId.SIDEBAR.MENU_ITEM_TUTORIAL,
    },
  ];

  return (
    <List>
      {canUseUangJalanRevamp(user) && (
        <SidebarMenuItem
          to="/dashboard"
          selected={location.pathname === "/dashboard"}
          icon={<DashboardOutlined />}
          text={t("Dashboard" as TranslationKeys)}
          onNavigate={onNavigate}
          data-testid={TestId.SIDEBAR.MENU_ITEM_DASHBOARD}
        />
      )}
      <UangJalanMenu onNavigate={onNavigate} />
      {canUseUangJalanRevamp(user) && <SuratJalanMenu onNavigate={onNavigate} />}
      {menuItems.map((item) =>
        item.isTrucks ? (
          <TruckGroupsMenu
            key={item.text}
            truckGroups={truckGroups}
            userId={userId}
            loading={loading}
            onNavigate={onNavigate}
          />
        ) : item.isStaticValues ? (
          <StaticValuesMenu key={item.text} onNavigate={onNavigate} />
        ) : item.isLogislySpxOnCall ? (
          <LogislySpxOnCallMenu key={item.text} onNavigate={onNavigate} />
        ) : item.isDevTools ? (
          <DevToolsMenu key={item.text} onNavigate={onNavigate} />
        ) : (
          <SidebarMenuItem
            key={item.text}
            to={item.path}
            selected={location.pathname === item.path}
            icon={item.icon}
            text={item.text}
            onNavigate={onNavigate}
            data-testid={item.testId}
          />
        ),
      )}
    </List>
  );
};

export default SidebarMenu;
